import { useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { BriefcaseIcon, CheckCircleIcon, TruckIcon } from "@heroicons/react/24/outline";

import useNotification from "../notifications/useNotifications";
import useApiHelper from "../../hooks/useApiHelper";
import { IAllJobsResponse } from "../../models/Job";
import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
  PartAssemblyType,
} from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { complexityChipClasses, complexityShort } from "../../utils/vehicleDisplay";
import AssemblyTimer from "./AssemblyTimer";
import Avatar from "../user/Avatar";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useSignalR } from "../../contexts/SignalRContext";

interface AssemblyFlowProps {
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
}

const iconButtonClasses =
  "inline-flex h-5 w-5 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50";

function VehicleChip({
  vehicle,
  now,
}: {
  vehicle: IProductionDashboardBayVehicle;
  now?: DateTime;
}) {
  const { t } = useTranslation("production");
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const { activeJobSessionsByVehicle } = useSignalR();
  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];
  const [isOpeningJob, setIsOpeningJob] = useState(false);

  const handleOpenVehicle = (e: React.MouseEvent) => {
    e.stopPropagation();
    openInNewTab(`/vehicles/${vehicle.id}`);
  };

  const handleOpenJob = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpeningJob) return;

    setIsOpeningJob(true);
    try {
      const response = await get<IAllJobsResponse>(`/job/vehicle/${vehicle.id}`);
      const jobs = response.data ?? [];
      const latest = [...jobs].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )[0];

      if (!latest) {
        addNotification({
          variant: "warn",
          primaryText: t("No job found for this vehicle"),
        });
        return;
      }

      openInNewTab(`/jobs/${latest.id}`);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to load job for this vehicle"),
      });
    } finally {
      setIsOpeningJob(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-2 py-1.5">
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-xs font-semibold text-gray-900 truncate">
          {vehicle.vrm || t("No VRM")}
        </span>
        <span
          className={classNames(
            "text-[9px] font-semibold uppercase tracking-wide rounded px-1 py-0.5 shrink-0",
            complexityChipClasses(vehicle.complexity)
          )}
        >
          {complexityShort(vehicle.complexity)}
        </span>
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
      </div>
      {now && (
        <div className="mt-1 flex items-center justify-between gap-1">
          <AssemblyTimer vehicle={vehicle} now={now} size="sm" />
          {vehicle.currentAssemblyPartsTotal > 0 && (
            <span className="text-[10px] font-medium text-gray-500 tabular-nums shrink-0">
              {t("{{removed}}/{{total}} parts", {
                removed: vehicle.currentAssemblyPartsRemoved,
                total: vehicle.currentAssemblyPartsTotal,
              })}
            </span>
          )}
        </div>
      )}
      <div className="mt-1 h-1 w-full rounded bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${Math.min(100, vehicle.percentComplete)}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-1">
        {activeJobUsers.length > 0 ? (
          <div className="flex items-center gap-0.5">
            <WrenchScrewdriverIcon className="w-3 h-3 text-amber-500 shrink-0" />
            <div className="flex">
              {activeJobUsers.map((userId, key) => (
                <Avatar key={key} userId={userId} classes="w-5 h-5 rounded-full [&:not(:first-child)]:-ml-1" textClasses="text-[14px]" />
              ))}
            </div>
          </div>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-x-1">
          <button
            type="button"
            onClick={handleOpenVehicle}
            className={iconButtonClasses}
            title={t("Open vehicle")}
          >
            <TruckIcon className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={handleOpenJob}
            disabled={isOpeningJob}
            className={iconButtonClasses}
            title={t("Open job")}
          >
            <BriefcaseIcon className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AssemblyColumn({
  name,
  label,
  vehicles,
  now,
  grow = true,
}: {
  name: string;
  label: string;
  vehicles: IProductionDashboardBayVehicle[];
  now: DateTime;
  grow?: boolean;
}) {
  const { t } = useTranslation("production");
  const isActive = vehicles.length > 0;
  const isNonAssembly = name === "Non-Assembly";
  return (
    <div
      className={classNames(
        "min-w-[140px] border rounded p-2",
        grow && "flex-1",
        isActive ? "bg-emerald-50 border-emerald-300" : "bg-gray-50 border-gray-200"
      )}
    >
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {isNonAssembly ? t("Other") : label}
          </div>
          <div className="text-xs font-semibold text-gray-800 truncate">{name}</div>
        </div>
      </div>
      {isActive ? (
        <div className="flex flex-col gap-1.5">
          {vehicles.map((v) => (
            <VehicleChip key={v.id} vehicle={v} now={now} />
          ))}
        </div>
      ) : (
        <div className="text-[10px] text-gray-400 italic py-2 text-center">—</div>
      )}
    </div>
  );
}

function BayFlowLane({
  bay,
  assemblies,
  now,
}: {
  bay: IProductionDashboardBay;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
}) {
  const { t } = useTranslation("production");
  const vehicles = bay.currentVehicles ?? [];
  const inProgress = vehicles.filter((v) => v.percentComplete < 100);
  const completed = vehicles.filter((v) => v.percentComplete >= 100);

  const byAssembly = new Map<string, IProductionDashboardBayVehicle[]>();
  inProgress.forEach((v) => {
    if (!v.currentAssemblyName) return;
    const list = byAssembly.get(v.currentAssemblyName) ?? [];
    list.push(v);
    byAssembly.set(v.currentAssemblyName, list);
  });

  const hasAny = vehicles.length > 0;

  // The overall flow order is preserved; the global index drives the "Assembly N"
  // label so sub assemblies stay numbered in their true position even though they
  // render in a separate row underneath the main assemblies.
  const indexed = assemblies.map((a, idx) => ({
    ...a,
    label: t("Assembly {{number}}", { number: idx + 1 }),
  }));
  const mainAssemblies = indexed.filter((a) => a.type !== PartAssemblyType.Sub);
  const subAssemblies = indexed.filter((a) => a.type === PartAssemblyType.Sub);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 truncate">{bay.name}</h3>
        {hasAny ? (
          <span className="text-[11px] text-gray-500 shrink-0">
            {t("{{count}} in progress", { count: inProgress.length })}
            {completed.length > 0 && t(", {{count}} complete", { count: completed.length })}
          </span>
        ) : (
          <span className="text-[11px] text-gray-400 shrink-0 italic">{t("No vehicle")}</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="flex flex-col gap-2 min-w-full">
          {/* Main assemblies flow left to right, ending in the Complete column */}
          <div className="flex gap-2">
            {mainAssemblies.map((a) => (
              <AssemblyColumn
                key={a.name}
                name={a.name}
                label={a.label}
                vehicles={byAssembly.get(a.name) ?? []}
                now={now}
              />
            ))}

            <div
              className={classNames(
                "flex-1 min-w-[140px] border rounded p-2",
                completed.length > 0 ? "bg-blue-50 border-blue-300" : "bg-blue-50/40 border-blue-100"
              )}
            >
              <div className="flex items-center gap-1 mb-1.5">
                <CheckCircleIcon
                  className={classNames(
                    "h-3.5 w-3.5 shrink-0",
                    completed.length > 0 ? "text-blue-600" : "text-blue-300"
                  )}
                />
                <div
                  className={classNames(
                    "text-xs font-semibold",
                    completed.length > 0 ? "text-blue-800" : "text-blue-400"
                  )}
                >
                  {t("Complete")}
                </div>
              </div>
              {completed.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {completed.map((v) => (
                    <VehicleChip key={v.id} vehicle={v} />
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-blue-300 italic py-2 text-center">—</div>
              )}
            </div>
          </div>

          {/* Sub assemblies sit in a row underneath, keeping their flow-order numbering */}
          {subAssemblies.length > 0 && (
            <div className="flex gap-2">
              {subAssemblies.map((a) => (
                <AssemblyColumn
                  key={a.name}
                  name={a.name}
                  label={a.label}
                  vehicles={byAssembly.get(a.name) ?? []}
                  now={now}
                  grow={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AssemblyFlow({ bays, assemblies, now }: AssemblyFlowProps) {
  if (assemblies.length === 0 || bays.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {bays.map((bay) => (
        <BayFlowLane key={bay.id} bay={bay} assemblies={assemblies} now={now} />
      ))}
    </div>
  );
}
